/**
 * BD: new 受診者情報履歴 row `available_to` when X = 保険証資格取得日 from 「受診者履歴有効期限設定」modal.
 * Compare X to temporary PI periods. The oldest PI period starts from the oldest
 * 保険証資格取得日; later PI starts are derived from previous available_to + 1.
 */

import type { PatientFormData } from '@pages/main/patient/types';
import {
  addOneDayYmd,
  isEmptyHistoryYmd,
  normalizeHistoryYmd as ymd,
  subOneDayYmd,
} from '@pages/main/patient/utils/patientHistoryHelpers';

function minHealthInsuranceQualificationYmd(cards: PatientFormData['health_insurance_cards']): string {
  let m = '';
  for (const c of cards ?? []) {
    const q = ymd(c.qualification_date);
    if (!q || isEmptyHistoryYmd(q)) {
      continue;
    }
    if (!m || q < m) {
      m = q;
    }
  }
  return m;
}

const OPEN_START = '0001-01-01';
const OPEN_END = '2100-12-31';

function normalizeAvailableTo(value: string | null | undefined): string {
  const to = ymd(value);
  return to && !isEmptyHistoryYmd(to) ? to : '';
}

function normalizeAvailableFrom(value: string | null | undefined): string {
  const from = ymd(value);
  return from && !isEmptyHistoryYmd(from) ? from : '';
}

function buildPatientInformationVirtualPeriods(patientData: PatientFormData): Array<{
  from: string;
  to: string;
}> {
  const rows = [...(patientData.patient_informations ?? [])].sort((a, b) => {
    const aTo = normalizeAvailableTo(a.available_to) || OPEN_END;
    const bTo = normalizeAvailableTo(b.available_to) || OPEN_END;
    return aTo.localeCompare(bTo) || (Number(a.id) || 0) - (Number(b.id) || 0);
  });

  const periods: Array<{ from: string; to: string }> = [];
  const oldestHicStart = minHealthInsuranceQualificationYmd(patientData.health_insurance_cards);
  let nextFrom = '';
  for (const row of rows) {
    const storedFrom = normalizeAvailableFrom(row.available_from);
    const to = normalizeAvailableTo(row.available_to);
    const from =
      storedFrom ||
      (periods.length === 0 && oldestHicStart && (!to || oldestHicStart <= to)
        ? oldestHicStart
        : nextFrom);
    periods.push({ from, to });
    nextFrom = to ? addOneDayYmd(to) : '';
  }

  return periods;
}

/**
 * @returns `''` = open end (null); otherwise YYYY-MM-DD for new row `available_to`.
 */
export function computeSinglePatientInformationHistoryNewAvailableToYmd(
  patientData: PatientFormData,
  xYmd: string,
): string {
  const x = ymd(xYmd);
  if (!x) {
    return '';
  }

  for (const period of buildPatientInformationVirtualPeriods(patientData)) {
    if (period.from && x < period.from) {
      return subOneDayYmd(period.from);
    }

    const effectiveFrom = period.from || OPEN_START;
    const effectiveTo = period.to || OPEN_END;
    if (effectiveFrom <= x && x <= effectiveTo) {
      return period.to;
    }
  }

  return '';
}
